import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  fullDate,
  get,
  groupDigits,
  italic,
  type LegalValues,
  mainTitle,
  mlaPageRange,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * Federal regulations — the Code of Federal Regulations and Federal Register rules —
 * in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7 §11.7 (Publication Manual p. 365), as reproduced by Liberty University,
 *   Columbus State and UMGC: codified — "Title or Number, Volume C.F.R. § xxx (Year).
 *   URL" ("Protection of Human Subjects, 45 C.F.R. § 46 (2009)."); not yet codified —
 *   "Title or Number, Volume F.R. Page (proposed Month Day, Year) (to be codified at
 *   Volume C.F.R. § xxx). URL". APA's manual abbreviates the Federal Register "F.R." and
 *   prints its page numbers without commas. In text: title and year.
 * - Bluebook 22d ed. rule 14.2 and Bluepages B14 (Cornell LII §2-410 and §2-420;
 *   Kirwin's Bluebook excerpts; LLSDC): "46 C.F.R. § 166.01 (2009)."; "Federal
 *   Acquisition Regulations for National Aeronautics and Space Administration, 55 Fed.
 *   Reg. 52,782 (Dec. 21, 1990) (to be codified at 48 C.F.R. pt. 1)." Proposed rules
 *   carry "(proposed [date])". Five-digit page numbers take commas (rule 6.2).
 * - Chicago 18: §14.170 relies on the Bluebook for legal sources; notes only.
 * - MLA 9: MLA Style Center, "Documenting Legal Works" and "How do I cite a state law?"
 *   — cite the version consulted; a law or code title is roman even inside a container
 *   ("Title 17. United States Code, U.S. Government Publishing Office, 2011, www…");
 *   the Federal Register is italic, with volume, issue, date and pages, as in the Style
 *   Center's executive-order example.
 */

export const CFR_FIELDS = {
  name: "name",
  title: "cfrTitle",
  unit: "unit",
  locator: "locator",
  year: "year",
  agency: "agency",
  website: "website",
  url: "url",
} as const;

export const FEDERAL_REGISTER_FIELDS = {
  name: "name",
  shortTitle: "shortTitle",
  ruleType: "ruleType",
  volume: "volume",
  issue: "issue",
  page: "page",
  lastPage: "lastPage",
  date: "date",
  codTitle: "codTitle",
  codUnit: "codUnit",
  codLocator: "codLocator",
  agency: "agency",
  url: "url",
} as const;

export interface RegulationLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

const RE_LOCATOR_PREFIX = /^(§+|pt\.?|part|sec\.?|section)\s*/i;

function cleanLocator(value: string): string {
  return value.replace(RE_LOCATOR_PREFIX, "").trim();
}

/** "§ 46.102" or "pt. 46" — Bluebook's marks for a section and a whole part. */
function legalLocator(unit: string, locator: string): string {
  return unit === "part" ? `pt. ${locator}` : `§ ${locator}`;
}

function mlaAuthor(agency: string): CitationParts {
  return agency ? [plain(`United States, ${agency}. `)] : [];
}

function mlaInText(agency: string, shortTitle: string): CitationParts {
  return [plain(agency ? `(United States, ${shortTitle})` : `(${shortTitle})`)];
}

/* ------------------------------------------------------------------- C.F.R. */

export function cfrIsReady(values: LegalValues): boolean {
  return (
    get(values, CFR_FIELDS.title) !== "" &&
    cleanLocator(get(values, CFR_FIELDS.locator)) !== "" &&
    get(values, CFR_FIELDS.year) !== ""
  );
}

export function formatCfrRegulation(
  values: LegalValues,
  labels: RegulationLabels
): CitationOutput[] {
  const F = CFR_FIELDS;
  const name = get(values, F.name);
  const title = get(values, F.title);
  const unit = get(values, F.unit) || "section";
  const locator = cleanLocator(get(values, F.locator));
  const year = get(values, F.year);
  const agency = get(values, F.agency);
  const website = get(values, F.website);
  const url = get(values, F.url);

  const code = `${title} C.F.R. ${legalLocator(unit, locator)} (${year})`;
  const cite = name ? `${name}, ${code}` : code;
  const short = name || `${title} C.F.R. ${legalLocator(unit, locator)}`;

  const unitWord = unit === "part" ? "part" : "section";
  const number = `title ${title}, ${unitWord} ${locator}, ${year}`;
  const mla: CitationParts = [...mlaAuthor(agency)];
  if (name) {
    mla.push(plain(`${name}${closer(name)} `));
    mla.push(plain(`Code of Federal Regulations, ${number}. `));
  } else {
    mla.push(
      plain(
        `Code of Federal Regulations. Title ${title}, ${unitWord} ${locator}, ${year}. `
      )
    );
  }
  if (website) {
    mla.push(italic(website));
    mla.push(plain(url ? `, ${mlaUrl(url)}.` : "."));
  } else if (url) {
    mla.push(plain(`${mlaUrl(url)}.`));
  }

  return [
    {
      key: "apa",
      label: labels.apa,
      entries: [
        {
          label: labels.referenceList,
          parts: [plain(`${cite}.${url ? ` ${url}` : ""}`)],
        },
        { label: labels.parenthetical, parts: [plain(`(${short}, ${year})`)] },
        { label: labels.narrative, parts: [plain(`${short} (${year})`)] },
      ],
      note: labels.apaNote,
    },
    {
      key: "mla",
      label: labels.mla,
      entries: [
        { label: labels.worksCited, parts: tidyParts(mla) },
        {
          label: labels.inText,
          parts: mlaInText(agency, name || "Code of Federal Regulations"),
        },
      ],
      note: labels.mlaNote,
    },
    {
      key: "chicago",
      label: labels.chicago,
      entries: [
        {
          label: labels.footnote,
          parts: [plain(`${cite}${url ? `, ${url}` : ""}.`)],
        },
      ],
      note: labels.chicagoNote,
    },
  ];
}

/* ---------------------------------------------------------- Federal Register */

export function federalRegisterIsReady(values: LegalValues): boolean {
  const F = FEDERAL_REGISTER_FIELDS;
  return (
    get(values, F.name) !== "" &&
    get(values, F.volume) !== "" &&
    get(values, F.page) !== "" &&
    parsePartialDate(get(values, F.date)) !== null
  );
}

/** "(to be codified at 29 C.F.R. pt. 541)" — empty when no target is given. */
function codification(values: LegalValues): string {
  const F = FEDERAL_REGISTER_FIELDS;
  const title = get(values, F.codTitle);
  const locator = cleanLocator(get(values, F.codLocator));
  if (title === "" || locator === "") {
    return "";
  }
  const unit = get(values, F.codUnit) || "part";
  return ` (to be codified at ${title} C.F.R. ${legalLocator(unit, locator)})`;
}

export function formatFederalRegisterRule(
  values: LegalValues,
  labels: RegulationLabels
): CitationOutput[] {
  const F = FEDERAL_REGISTER_FIELDS;
  const name = get(values, F.name);
  const short = get(values, F.shortTitle) || mainTitle(name);
  const proposed = get(values, F.ruleType) === "proposed";
  const volume = get(values, F.volume);
  const page = get(values, F.page);
  const date = parsePartialDate(get(values, F.date));
  const year = date ? String(date.year) : "n.d.";
  const agency = get(values, F.agency);
  const url = get(values, F.url);
  const when = `${proposed ? "proposed " : ""}${fullDate(date)}`;
  const toBeCodified = codification(values);

  const apaCite = `${name}, ${volume} F.R. ${page} (${when})${toBeCodified}`;
  const chicagoCite = `${name}, ${volume} Fed. Reg. ${groupDigits(page)} (${when})${toBeCodified}`;

  const range = mlaPageRange(page, get(values, F.lastPage));
  const details = [
    `vol. ${volume}`,
    get(values, F.issue) ? `no. ${get(values, F.issue)}` : "",
    mlaDate(date),
    range.includes("–") ? `pp. ${range}` : `p. ${range}`,
    mlaUrl(url),
  ].filter(Boolean);
  const mla: CitationParts = [
    ...mlaAuthor(agency),
    plain(`${name}${closer(name)} `),
    italic("Federal Register"),
    plain(`, ${details.join(", ")}.`),
  ];
  if (proposed) {
    mla.push(plain(" Proposed rule."));
  }

  return [
    {
      key: "apa",
      label: labels.apa,
      entries: [
        {
          label: labels.referenceList,
          parts: [plain(`${apaCite}.${url ? ` ${url}` : ""}`)],
        },
        { label: labels.parenthetical, parts: [plain(`(${short}, ${year})`)] },
        { label: labels.narrative, parts: [plain(`${short} (${year})`)] },
      ],
      note: labels.apaNote,
    },
    {
      key: "mla",
      label: labels.mla,
      entries: [
        { label: labels.worksCited, parts: tidyParts(mla) },
        { label: labels.inText, parts: mlaInText(agency, short) },
      ],
      note: labels.mlaNote,
    },
    {
      key: "chicago",
      label: labels.chicago,
      entries: [
        {
          label: labels.footnote,
          parts: [plain(`${chicagoCite}${url ? `, ${url}` : ""}.`)],
        },
      ],
      note: labels.chicagoNote,
    },
  ];
}
